import React, { useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'

interface SheetProps {
  open: boolean
  onClose(): void
  children: React.ReactNode
  title?: string
}

export function Sheet({ open, onClose, children, title }: SheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null)
  const dragStartY = useRef<number | null>(null)
  const dragCurrentY = useRef(0)

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const onHandlePointerDown = useCallback((e: React.PointerEvent) => {
    dragStartY.current = e.clientY
    dragCurrentY.current = 0
    const el = e.currentTarget as HTMLElement
    el.setPointerCapture(e.pointerId)

    const onMove = (ev: PointerEvent) => {
      if (dragStartY.current === null) return
      const dy = ev.clientY - dragStartY.current
      if (dy > 0 && sheetRef.current) {
        dragCurrentY.current = dy
        sheetRef.current.style.transform = `translateY(${dy}px)`
      }
    }

    const onUp = () => {
      if (dragCurrentY.current > 80) {
        onClose()
      } else if (sheetRef.current) {
        sheetRef.current.style.transform = 'translateY(0)'
      }
      dragStartY.current = null
      dragCurrentY.current = 0
      el.removeEventListener('pointermove', onMove)
      el.removeEventListener('pointerup', onUp)
      el.removeEventListener('pointercancel', onUp)
    }

    el.addEventListener('pointermove', onMove)
    el.addEventListener('pointerup', onUp)
    el.addEventListener('pointercancel', onUp)
  }, [onClose])

  if (!open) return null

  return createPortal(
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 1000,
        }}
      />
      <div
        ref={sheetRef}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1001,
          background: 'var(--color-paper)',
          borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.15)',
          animation: 'sheet-slide-up 0.25s ease',
        }}
      >
        <div
          onPointerDown={onHandlePointerDown}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '12px 16px 8px',
            cursor: 'grab',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 36,
              height: 4,
              borderRadius: 2,
              background: 'var(--color-ink-faint)',
            }}
          />
          {title && (
            <p
              style={{
                margin: '8px 0 0',
                fontFamily: 'var(--font-serif)',
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--color-ink)',
              }}
            >
              {title}
            </p>
          )}
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>{children}</div>
      </div>
      <style>{`
        @keyframes sheet-slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </>,
    document.body
  )
}
